// fetchShopSections.js
'use strict';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import { SHOP_ID } from '../resource/shopConstants.js';

import getMeData from '../selector/getMeData.js';
import getOperationData from '../selector/getOperationData.js';
import mergeMeData from '../action/mergeMeData.js';

import {
  MERGE_OPERATION_DATA,
  SET_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Fetch shop sections for an entry point.
 * The backend resolves the section layout from the user behavior (entrypoint)
 * and purchase status, and tells the frontend whether it is a quickpay shop
 * via `modalType`.
 * @kind action
 * @param {object} payload - payload.
 * @param {string} payload.entrypoint - shop entry point.
 * @param {string} payload.currency - using currency.
 * @return {Promise} Action promise.
 */
const fetchShopSections =
  ({ entrypoint, currency } = {}) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    const networkingSelectPath = [
      'fetch',
      'shopSections',
      entrypoint,
      currency,
    ];
    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
    const fetchUrl = getResourceUrl({ endpoint: '/shop/sections' });
    if (entrypoint) {
      fetchUrl.searchParams.set('entrypoint', entrypoint);
    }
    if (currency) {
      fetchUrl.searchParams.set('currency', currency);
    }

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);
      if (!response.ok) {
        await handleFetchError({ response });
      }
      const { sections, modalType, purchaseCount } = await response.json();
      // A quick currency/entrypoint switch can leave an older request in flight;
      // skip the shared writes if the selection moved on, so a late response
      // doesn't overwrite the current one (last-write-wins). Networking success
      // below is per-(entrypoint, currency), so it still dispatches.
      const isStale =
        currency !== getOperationData(getState(), ['shop'], 'currency') ||
        entrypoint !==
          getOperationData(
            getState(),
            ['shop', SHOP_ID.addvalue],
            'entrypoint'
          );
      if (!isStale) {
        // Merge (not replace) so the ongoing order fields on this node
        // (selectedDiamondPackId, submitOrderId) survive; replacing would wipe
        // them and make the auto-checkout fire again.
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['shop', SHOP_ID.addvalue],
            data: { entrypoint, modalType },
          },
        });
        // Set sections separately: merging a List merges by index, so a shorter
        // new list would leave the previous currency's entries behind.
        dispatch({
          type: SET_OPERATION_DATA,
          payload: {
            selectPath: ['shop', SHOP_ID.addvalue, 'sections'],
            data: (sections || '').split(',').filter(section => !!section),
          },
        });
        dispatch(mergeMeData({ field: 'purchaseCount', value: purchaseCount }));
      }
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchShopSections;
